/**
 * CMS’ten gelen HTML için sunucu/istemci ortak sanitize (XSS azaltma).
 * `pages/api/render-component` ile aynı politika — tek kaynak.
 */
import sanitizeHtml from "sanitize-html";
import type { IOptions as SanitizeHtmlOptions } from "sanitize-html";

export const CMS_HTML_SANITIZE_OPTIONS: SanitizeHtmlOptions = {
  allowedTags: sanitizeHtml.defaults.allowedTags.concat([
    "img",
    "section",
    "header",
    "footer",
    "article",
    "figure",
    "figcaption",
    "main",
    "video",
    "source",
    "picture",
    "noscript",
    "svg",
    "path",
    "use",
    "dl",
    "dt",
    "dd",
  ]),
  allowedAttributes: {
    "*": ["class", "id", "title", "role", "data-*", "aria-*"],
    a: ["href", "name", "target", "rel", "title"],
    img: ["src", "alt", "title", "width", "height", "loading", "decoding"],
    iframe: ["src", "title", "width", "height", "allow", "allowfullscreen"],
    video: ["src", "controls", "poster", "width", "height", "preload"],
    source: ["src", "type", "media"],
    use: ["href", "xlink:href"],
    svg: [
      "width",
      "height",
      "viewBox",
      "fill",
      "stroke",
      "stroke-width",
      "stroke-linecap",
      "stroke-linejoin",
      "xmlns",
      "aria-hidden",
      "focusable",
      "preserveAspectRatio",
    ],
    path: [
      "d",
      "fill",
      "stroke",
      "stroke-width",
      "stroke-linecap",
      "stroke-linejoin",
      "clip-rule",
      "fill-rule",
    ],
    circle: ["cx", "cy", "r", "fill", "stroke", "stroke-width"],
    rect: ["x", "y", "rx", "ry", "width", "height", "fill", "stroke", "stroke-width"],
    polygon: ["points", "fill", "stroke", "stroke-width"],
    polyline: ["points", "fill", "stroke", "stroke-width"],
    line: ["x1", "y1", "x2", "y2", "stroke", "stroke-width", "stroke-linecap"],
    g: ["fill", "stroke", "stroke-width", "clip-path"],
  },
  allowedSchemes: ["http", "https", "mailto", "tel", "data"],
  allowedSchemesByTag: {
    img: ["http", "https", "data"],
    source: ["http", "https", "data"],
    video: ["http", "https"],
  },
  disallowedTagsMode: "discard",
  allowProtocolRelative: false,
  transformTags: {
    a: (tagName, attribs) => {
      if (attribs.target === "_blank") {
        const rel = attribs.rel || "";
        const relValues = new Set(
          rel
            .split(/\s+/)
            .filter(Boolean)
            .map((value) => value.toLowerCase()),
        );
        relValues.add("noopener");
        relValues.add("noreferrer");
        attribs.rel = Array.from(relValues).join(" ");
      }
      return { tagName, attribs };
    },
  },
};

export function sanitizeCmsHtml(input: string | null | undefined): string {
  if (input == null || typeof input !== "string") {
    return "";
  }
  return sanitizeHtml(input, CMS_HTML_SANITIZE_OPTIONS);
}
